import {getLevelColor} from '../../constants'
import {OfferNode} from '../../types'
import {BrokereeKpiPanel} from '../BrokereeKpiPanel'
import {nodeAnchorId, useKpiPanel} from '../KpiPanelContext'
import {OfferCardActions} from './OfferCardActions'
import {OfferCardHeader} from './OfferCardHeader'
import {OfferStatsGrid} from './OfferStatsGrid'
import {Card, TopRow} from './OfferTreeCard.styled'

type OfferTreeCardProps = {
  node: OfferNode
  hasChildren: boolean
  expanded: boolean
  onToggleExpand: () => void
}

/**
 * A single offer card: an identity header (name, badges, meta), Copy Link / Invest
 * actions and a stats grid. The left border is tinted by the node's depth.
 *
 * @param node - The offer tree node to render (offer data and tree depth).
 * @param hasChildren - Whether the node has sub-offers, i.e. whether to show the chevron.
 * @param expanded - Current expand state, forwarded to the header's chevron.
 * @param onToggleExpand - Invoked when the expand/collapse chevron is clicked.
 */
export const OfferTreeCard = ({
  node,
  hasChildren,
  expanded,
  onToggleExpand,
}: OfferTreeCardProps) => {
  const {offer, level} = node
  const {isOpen} = useKpiPanel()
  const kpiOpen = isOpen(String(offer.id))

  return (
    <Card
      id={nodeAnchorId(String(offer.id))}
      data-testid="offer-tree-card"
      style={{borderLeftColor: getLevelColor(level)}}
    >
      <TopRow>
        <OfferCardHeader
          offer={offer}
          level={level}
          hasChildren={hasChildren}
          expanded={expanded}
          onToggleExpand={onToggleExpand}
        />
        <OfferCardActions offer={offer} />
      </TopRow>
      <OfferStatsGrid offer={offer} />
      {/* Inline KPI panel (PT-5710): rendered inside the card so opening it grows only this
          card downward — sibling cards never shift horizontally and the page never scrolls. */}
      {kpiOpen && <BrokereeKpiPanel node={node} />}
    </Card>
  )
}
